<template>
  <div class="container-fluid py-4">
    <!-- Ad Component -->
    <GoogleAds />
    <div class="row justify-content-center">
      <div class="col-lg-10 col-md-12">
        <div class="card">
          <div class="card-body p-4">
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-4">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading details...</p>
            </div>

            <template v-else>
            <!-- User Greeting -->
            <div class="mb-4">
              <p class="mb-2">Hello <strong>{{ displayUserName }}</strong>,</p>
            </div>

            <!-- Volunteer Introduction -->
            <div class="mb-4">
              <p class="mb-3">{{ displayIntroText }}</p>
              <p class="mb-3">{{ displayInstructionsText }}</p>
            </div>

            <!-- Volunteer Opportunities Table -->
            <div class="mb-4" v-if="displayVolunteerOpportunities && displayVolunteerOpportunities.length > 0">
              <table class="table table-bordered">
                <thead>
                  <tr>
                    <th class="text-center">Sign Up</th>
                    <th>Workgroup</th>
                    <th>Date</th>
                    <th>Activity/Event</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(opportunity, index) in displayVolunteerOpportunities" :key="index">
                    <td class="text-center">
                      <MaterialButton 
                        color="success" 
                        variant="outline"
                        :disabled="signingUp"
                        @click="signUp(opportunity)"
                      >
                        {{ signingUp ? 'Signing Up...' : 'Sign Up' }}
                      </MaterialButton>
                    </td>
                    <td>{{ opportunity.workgroup }}</td>
                    <td>{{ opportunity.date }}</td>
                    <td>{{ opportunity.activity }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Thank You Message -->
            <div class="mb-4">
              <p class="mb-2">Thank you for serving.</p>
              <p class="mb-0"><strong>{{ displayLocationName }}</strong></p>
            </div>

            <!-- Login Link -->
            <router-link to="/sign-in">
              <MaterialButton color="success">
                Click here to login
              </MaterialButton>
            </router-link>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import MaterialButton from '@/components/common/MaterialButton.vue'
import GoogleAds from '@/components/GoogleAds/GoogleAds.vue'
import api from '@/config/api'

const route = useRoute()
const toast = useToast()

// State
const loading = ref(true)
const signingUp = ref(false)
const userName = ref('')
const introText = ref('')
const instructionsText = ref('')
const locationName = ref('')
const volunteerOpportunities = ref([])

// Mock fallback data
const mockUserName = 'Edward'
const mockIntroText = 'We are always in need of volunteers. Below are upcoming activities where we need volunteers.'
const mockInstructionsText = 'If you would like to volunteer, please click on the "Sign Up" link next to the activities that you are interested in. Clicking on the link will automatically sign you up.'
const mockLocationName = 'CS Test Church - Chicago main Branch'
const mockVolunteerOpportunities = [
  {
    workgroup: 'custodian [2 opening(s) available]',
    date: 'Jan 4, 2026 10:00:00 AM',
    activity: 'Sunday Morning Service -main'
  },
  {
    workgroup: 'custodian [2 opening(s) available]',
    date: 'Jan 18, 2026 10:00:00 AM',
    activity: 'Sunday Morning Service -main'
  }
]

// Computed display values (prefer API, fallback to mock)
const displayUserName = computed(() => userName.value || mockUserName)
const displayIntroText = computed(() => introText.value || mockIntroText)
const displayInstructionsText = computed(() => instructionsText.value || mockInstructionsText)
const displayLocationName = computed(() => locationName.value || mockLocationName)
const displayVolunteerOpportunities = computed(() =>
  (volunteerOpportunities.value && volunteerOpportunities.value.length > 0)
    ? volunteerOpportunities.value
    : mockVolunteerOpportunities
)

// Fetch page details on mount
const fetchInvitationDetails = async () => {
  const encodedString = route.params.encodedString
  if (!encodedString) {
    loading.value = false
    return
  }

  try {
    loading.value = true
    const response = await api.get(`/account/volunteerinvitation-details/cs/${encodedString}`)

    if (response.data) {
      const data = response.data

      userName.value = data.username || ''
      introText.value = data.introText || ''
      instructionsText.value = data.instructionsText || ''
      locationName.value = data.locationName || ''
      if (data.volunteerOpportunities && Array.isArray(data.volunteerOpportunities)) {
        volunteerOpportunities.value = data.volunteerOpportunities
      }
    }
  } catch (error) {
    console.error('Error fetching volunteer invitation details:', error)
  } finally {
    loading.value = false
  }
}

// Sign up action
const signUp = async (opportunity) => {
  const encodedString = route.params.encodedString
  if (!encodedString || signingUp.value) return

  signingUp.value = true
  try {
    const response = await api.post(`/account/volunteerinvitation-signup/cs/${encodedString}`, {
      workgroup: opportunity.workgroup,
      date: opportunity.date,
      activity: opportunity.activity,
      opportunityId: opportunity.id
    })
    const msg = response.data?.message || `Successfully signed up for ${opportunity.activity} on ${opportunity.date}`
    toast.success(msg, { position: 'top-right' })
  } catch (error) {
    console.error('Error signing up:', error)
    const errMsg = error.response?.data?.message || error.message || 'Failed to sign up.'
    toast.error(errMsg, { position: 'top-right' })
  } finally {
    signingUp.value = false
  }
}

onMounted(async () => {
  await fetchInvitationDetails()
})
</script>

<style scoped>
.table {
  width: 100%;
  margin-bottom: 1rem;
  color: #212529;
}

.table-bordered {
  border: 1px solid #dee2e6;
}

.table-bordered th,
.table-bordered td {
  border: 1px solid #dee2e6;
  padding: 0.75rem;
}

.table thead th {
  vertical-align: bottom;
  border-bottom: 2px solid #dee2e6;
  background-color: #f8f9fa;
}
</style>

